import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import { Header, Footer } from "../components/Layout";
import { VoiceInput } from "../components/Widgets";
import { DemoExpress } from "../components/JurisWidgets";
import { CommentCaMarche } from "../components/CommentCaMarche";
import { QuotaBanner } from "../components/QuotaBanner";
import { DocumentScanner } from "../components/DocumentScanner";
import { AnalysisOverlay } from "../components/AnalysisOverlay";
import { BetaTestersRegistry } from "../components/BetaTestersRegistry";
import { Zap, ShieldCheck, Check, Camera, Trophy, ArrowRight, Loader2, Sparkles, AlertTriangle } from "lucide-react";

const CASE_TYPES = [
  { v: "bail_litige", label: "🏠 Logement / Bail locatif", hint: "Caution, charges, réparations" },
  { v: "stationnement_fps", label: "🚗 Stationnement / FPS", hint: "Contestation avis indu" },
  { v: "consommation", label: "🛒 Consommation & Factures", hint: "Litiges marchands, abonnements" },
  { v: "travail", label: "💼 Litige Travail / Contrat", hint: "Rupture, attestations" },
];

// Templates guidés par type de dossier (append, pas d'écrasement)
const GUIDED_TEMPLATES = {
  bail_litige: `Mon propriétaire [refuse / tarde] à me rendre ma caution de ___ € depuis le [date de fin de bail].
L'état des lieux de sortie a été réalisé le [date].
Motif invoqué par le bailleur : [dégradations / impayés / autre].
Pièces en ma possession : bail signé, état des lieux d'entrée + sortie, quittances de loyer, courriers échangés.
Démarches déjà effectuées : [recommandé le ___ / aucune].`,
  stationnement_fps: `J'ai reçu un avis de paiement FPS de ___ € le [date] à [adresse exacte].
Motif indiqué sur l'avis : [stationnement payant non réglé / dépassement / autre].
Ma situation : [j'avais un ticket valide / je bénéficie d'une carte résident / véhicule vendu / autre].
Pièces en ma possession : avis FPS, ticket de stationnement, carte grise, carte résident si applicable.
Démarches déjà effectuées : [RAPO le ___ / aucune].`,
  consommation: `J'ai souscrit un contrat / acheté un produit auprès de [enseigne / vendeur] le [date] pour ___ €.
Nature du problème : [livraison non effectuée / produit défectueux / facturation abusive / abonnement non résilié malgré demande].
Date du problème constaté : [date].
Démarches déjà effectuées : [SAV contacté le ___ / recommandé le ___ / médiateur / aucune].
Pièces en ma possession : facture, bon de commande, échanges mail/SMS, CGV.`,
  travail: `Mon employeur [nom entreprise] [refuse / tarde] à me fournir ___ depuis le [date].
Contexte : [rupture de contrat / solde de tout compte / attestation Pôle Emploi / certificat de travail / autre].
Date de fin de contrat : [date] · Type de rupture : [démission / licenciement / rupture conventionnelle / fin CDD].
Pièces en ma possession : contrat de travail, derniers bulletins de salaire, courriers échangés.
Démarches déjà effectuées : [recommandé le ___ / inspection du travail / aucune].`,
};

// Détection d'énoncé insuffisant : < 60 car OU manque indice chiffré/temporel critique
function analyseDescription(txt) {
  const t = (txt || "").trim();
  if (t.length < 60) return { incomplete: true, reason: "Votre description fait moins de 60 caractères" };
  const hasAmount = /\b\d{1,6}\s?(€|eur|euros)\b/i.test(t) || /\b(montant|caution|loyer|facture|amende|fps)\b/i.test(t);
  const hasDate = /\b(\d{1,2}[\/\-\.]\d{1,2}|\d{4}|janv|févr|mars|avril|mai|juin|juil|août|sept|oct|nov|déc|hier|aujourd|semaine|mois)\b/i.test(t);
  if (!hasAmount && !hasDate) return { incomplete: true, reason: "Il manque un montant (€) et une date dans votre description" };
  if (!hasAmount) return { incomplete: true, reason: "Il manque un montant (€) dans votre description" };
  if (!hasDate) return { incomplete: true, reason: "Il manque une date dans votre description" };
  return { incomplete: false, reason: "" };
}

export default function Landing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState("");
  const [caseType, setCaseType] = useState("bail_litige");
  const [uploadInfo, setUploadInfo] = useState("");
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [inlineError, setInlineError] = useState("");

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadInfo("Extraction en cours...");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadInfo(`✓ ${r.data.filename} — ${r.data.text?.length || 0} caractères extraits`);
      setDescription((d) => (d ? d + "\n\n--- Document joint ---\n" : "") + (r.data.text || ""));
      setInlineError("");
    } catch { setUploadInfo("Erreur d'extraction. Utilisez le champ texte."); }
  };

  const submit = async () => {
    setInlineError("");
    const trimmed = (description || "").trim();
    if (trimmed.length < 20) {
      setInlineError("Veuillez décrire brièvement votre litige ou déposer un document.");
      return;
    }
    // Vérification complétude : < 60 car OU manque montant/date → bloque et propose template
    const check = analyseDescription(trimmed);
    if (check.incomplete) {
      setInlineError(`${check.reason}. Utilisez le modèle guidé ci-dessous pour structurer votre dossier.`);
      return;
    }
    setLoading(true);
    setShowAnalysis(true);
    try {
      const r = await api.post("/dossier-flash/analyze", { description: trimmed, case_type: caseType });
      const dossierId = r.data?.dossier_id;
      if (dossierId) {
        // Redirection immédiate vers le Dossier Flash 4-blocs — pas d'écran intermédiaire
        navigate(`/dossier/${dossierId}`);
        return;
      }
      setShowAnalysis(false);
      setLoading(false);
      setInlineError("Réponse invalide du serveur. Réessayez.");
    } catch (e) {
      setShowAnalysis(false);
      setLoading(false);
      const detail = e.response?.data?.detail;
      setInlineError(detail || "Analyse indisponible. Réessayez dans un instant.");
    }
  };

  // Append le template guidé à la fin de la description (sans écraser)
  const appendTemplate = () => {
    const tpl = GUIDED_TEMPLATES[caseType] || GUIDED_TEMPLATES.bail_litige;
    setDescription((d) => {
      const trimmed = (d || "").trim();
      if (!trimmed) return tpl;
      if (trimmed.includes(tpl.substring(0, 40))) return d; // déjà présent
      return trimmed + "\n\n--- Modèle guidé ---\n" + tpl;
    });
    setInlineError("");
  };

  // Analyse live de la description pour afficher le hint template
  const liveCheck = analyseDescription(description);
  const showTemplateHint = description.trim().length >= 20 && liveCheck.incomplete && !loading;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {showAnalysis && <AnalysisOverlay />}
      <Header />
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-8">
        <div className="fade-up">
          <div className="j-label mb-3 flex items-center gap-2">
            <span className="pulse-dot"></span> Infrastructure B2C2B · Sources officielles
          </div>
          <h1 className="j-h1 font-heading" data-testid="hero-title">
            Votre dossier structuré<br/>
            <span className="text-[#10B981]">en 47 secondes.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-3xl">
            Extraction factuelle et structuration de pièces. <b>Sources officielles vérifiables : Légifrance, Judilibre et Service-Public.fr.</b>
          </p>
        </div>

        {/* BANNIÈRE JURY */}
        <div className="mt-6 p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white shadow-lg flex flex-col sm:flex-row sm:items-center gap-3" data-testid="jury-banner">
          <Trophy className="w-6 h-6 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-heading font-black text-base sm:text-lg">🏆 Membres du Jury Emergent</div>
            <div className="text-emerald-50 text-sm">Testez l'instruction express B2B en 47 secondes.</div>
          </div>
          <button onClick={() => navigate("/preinstruction")} className="min-h-[44px] px-5 py-2.5 rounded-xl bg-white text-emerald-700 font-black text-sm hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2 flex-shrink-0" data-testid="jury-cta-btn">
            Espace Pro & Jury <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-6">
          <DemoExpress onLoad={(s) => { setDescription(s.description); setCaseType(s.case_type === "amende" ? "stationnement_fps" : s.case_type); window.scrollTo({ top: 500, behavior: "smooth" }); }} />
        </div>

        <div className="mt-4"><QuotaBanner /></div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="card-citizen rounded-2xl p-6 sm:p-8 fade-up d-100" data-testid="citizen-intake">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-5 h-5 text-[#10B981]" />
                <span className="text-xs uppercase tracking-widest font-semibold text-[#10B981]">Intake Zéro Friction</span>
              </div>
              <h2 className="j-h2 font-heading">Décrivez votre situation</h2>

              <div className="mt-5 space-y-4">
                <div>
                  <label className="j-label mb-1 block">Type de dossier</label>
                  <select value={caseType} onChange={(e) => setCaseType(e.target.value)} className="j-select" data-testid="citizen-case-type">
                    {CASE_TYPES.map((c) => <option key={c.v} value={c.v}>{c.label} — {c.hint}</option>)}
                  </select>
                </div>

                <VoiceInput value={description} onChange={setDescription} placeholder="Décrivez votre situation ici… Ex: Mon propriétaire refuse de me rendre ma caution de 850€…" testId="citizen-description" rows={7} />

                {showTemplateHint && (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-start gap-3" data-testid="citizen-template-hint">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-black text-amber-900">Votre description manque d'éléments clés</div>
                      <div className="text-xs text-amber-800 mt-1">{liveCheck.reason}. Utilisez le modèle guidé adapté à votre dossier pour un diagnostic précis.</div>
                    </div>
                    <button type="button" onClick={appendTemplate} className="flex-shrink-0 min-h-[40px] px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 shadow-sm" data-testid="citizen-use-template-btn">
                      <Sparkles className="w-3.5 h-3.5" /> Utiliser le modèle guidé
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 items-center">
                  <label className="inline-flex items-center gap-2 text-sm text-[#10B981] font-semibold cursor-pointer hover:underline" data-testid="citizen-upload-label">
                    <Camera className="w-4 h-4" />
                    <span>Scanner / Déposer un document (Bail, Avis FPS, Facture…)</span>
                    <input type="file" accept=".pdf,image/*" className="hidden" onChange={handleUpload} data-testid="citizen-file-input" />
                  </label>
                  {uploadInfo && <div className="text-xs text-slate-500" data-testid="upload-info">{uploadInfo}</div>}
                </div>

                <button onClick={submit} disabled={loading} className="btn-emerald w-full text-lg min-h-[48px]" data-testid="citizen-submit-btn">
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Analyse documentaire en cours… (47s)</> : <><Zap className="w-5 h-5" /> Lancer mon analyse en 47s</>}
                </button>
                {inlineError && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="citizen-inline-error">
                    {inlineError}
                  </div>
                )}
                <div className="text-center text-xs text-slate-500">
                  Gratuit · Résultat en 47 secondes · Contrôle de complétude documentaire
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 fade-up d-200">
            <DocumentScanner onExtractText={(txt) => setDescription((d) => d && !d.includes(txt) ? d + "\n\n--- Document analysé ---\n" + txt : (d || txt))} />
            <div className="j-card">
              <div className="j-label mb-2 flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Juris-Guardian</div>
              <ul className="text-xs text-slate-600 space-y-2">
                <li><Check className="w-3 h-3 inline text-[#10B981] mr-1" /> Anonymisation RGPD automatique</li>
                <li><Check className="w-3 h-3 inline text-[#10B981] mr-1" /> Anti-hallucination Legifrance</li>
                <li><Check className="w-3 h-3 inline text-[#10B981] mr-1" /> Coffre-fort de preuves eIDAS-lite</li>
                <li><Check className="w-3 h-3 inline text-[#10B981] mr-1" /> Loi 71-1130 : aucun conseil personnalisé</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 border-t border-slate-200">
        <CommentCaMarche />
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 border-t border-slate-200">
        <div className="j-label mb-2">Tarification transparente</div>
        <h2 className="j-h2 font-heading mb-8">Cohérente avec le produit</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { name: "Scan & Structuration express", price: "0 €", desc: "Contrôle de complétude et qualification factuelle en 47 secondes.", testid: "price-scan" },
            { name: "Pack Résolution Citoyen", price: "8,99 €", desc: "Mise en demeure + modèle type personnalisé, prêt à envoyer.", testid: "price-pack" },
            { name: "Espace Pro B2B", price: "49 €/mois", desc: "Gestionnaires et syndics : pré-instruction illimitée + historique.", testid: "price-pro" },
          ].map((p) => (
            <div key={p.name} className="j-card" data-testid={p.testid}>
              <div className="font-heading font-black text-lg text-[#1E293B]">{p.name}</div>
              <div className="font-heading font-black text-2xl text-[#10B981] mt-1">{p.price}</div>
              <div className="text-sm text-slate-600 mt-2">{p.desc}</div>
            </div>
          ))}
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 border-t border-slate-200">
        <BetaTestersRegistry />
      </section>
      <Footer />
    </div>
  );
}
